import { useSyncExternalStore } from "react";

// Live (reactive) display prefs: 12h/24h start times + in-game clock direction.
// Mirrored to localStorage (instant, per device) and the account (synced across devices).
const EVT = "rosterly-timeprefs";
const KEYS = { time_format: "rosterly_time_format", clock_mode: "rosterly_clock_mode", tz_label: "rosterly_tz_label" };
// Account pref: show the time-zone name after start times ("7:00 PM EST") — off by default.
export const prefTzLabel = () => read("tz_label", "off") === "on";
const read = (k, d) => { try { return localStorage.getItem(KEYS[k]) || d; } catch { return d; } };

export function setTimePref(key, val) {
    try { if (val) localStorage.setItem(KEYS[key], val); } catch { /* noop */ }
    window.dispatchEvent(new Event(EVT));
}

// Apply the account's saved values on this device (called whenever the account loads).
export function hydrateTimePrefs(profile) {
    if (!profile) return;
    let changed = false;
    ["time_format", "clock_mode", "tz_label"].forEach((k) => {
        const v = profile[k];
        if (v && read(k, "") !== v) { try { localStorage.setItem(KEYS[k], v); changed = true; } catch { /* noop */ } }
    });
    if (changed) window.dispatchEvent(new Event(EVT));
}

const subscribe = (cb) => {
    const onStorage = (e) => { if (!e.key || Object.values(KEYS).includes(e.key)) cb(); };
    window.addEventListener(EVT, cb);
    window.addEventListener("storage", onStorage);
    return () => { window.removeEventListener(EVT, cb); window.removeEventListener("storage", onStorage); };
};
const snap = () => `${read("time_format", "12h")}|${read("clock_mode", "elapsed")}|${read("tz_label", "off")}`;

export function useTimePrefs() {
    const s = useSyncExternalStore(subscribe, snap, snap);
    const [tf, cm, tz] = s.split("|");
    return { hour12: tf !== "24h", timeFormat: tf, clockMode: cm === "remaining" ? "remaining" : "elapsed", tzLabel: tz === "on" };
}

// Start time of a game (UTC ISO) in the viewer's local zone, honouring 12h/24h.
export function fmtStartTime(utc, hour12, extra = {}) {
    if (!utc) return "";
    try { return new Date(utc).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", hour12, ...(prefTzLabel() ? { timeZoneName: "short" } : {}), ...extra }); } catch { return ""; }
}
export function fmtStartDateTime(utc, hour12, dateOpts = { weekday: "short", month: "short", day: "numeric" }) {
    if (!utc) return "";
    try { return new Date(utc).toLocaleString(undefined, { ...dateOpts, hour: "numeric", minute: "2-digit", hour12, ...(prefTzLabel() ? { timeZoneName: "short" } : {}) }); } catch { return ""; }
}

// In-game event clock. `time` is ELAPSED in the period ("08:21"); "remaining" mode
// flips it to time left ("11:39"). `periodLen` in seconds (1200 reg, 300 NHL reg-season OT).
export function fmtClock(time, mode, periodLen = 1200) {
    if (!time || typeof time !== "string" || !/^\d{1,2}:\d{2}$/.test(time.trim())) return time || "";
    const [m, s] = time.trim().split(":").map(Number);
    let secs = m * 60 + s;
    if (mode === "remaining") secs = Math.max(0, periodLen - secs);
    return `${String(Math.floor(secs / 60)).padStart(2, "0")}:${String(secs % 60).padStart(2, "0")}`;
}
// Same, but the source is a live clock counting DOWN ("11:39" left).
export function fmtClockFromRemaining(time, mode, periodLen = 1200) {
    if (!time || !/^\d{1,2}:\d{2}$/.test(String(time).trim())) return time || "";
    if (mode === "remaining") return fmtClock(String(time).trim(), "elapsed");
    const [m, s] = String(time).trim().split(":").map(Number);
    return fmtClock(`${Math.floor(Math.max(0, periodLen - (m * 60 + s)) / 60)}:${String(Math.max(0, periodLen - (m * 60 + s)) % 60).padStart(2, "0")}`, "elapsed");
}
